import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useContaCilEmpresaContexto } from '../../../../hooks/useContaCilEmpresas';
import { IEmpresaOption } from '../../../../hooks/useContaCilImportar';

/**
 * Resolve `?empresa=<id>` do query string em um `IEmpresaOption` pronto para
 * uso em AsyncSelect — atalhos da pagina Empresas entram em /conciliar,
 * /extratos, /plano-contas, /importar com esse parametro.
 *
 * Retorna `{ empresa, loading }`. Quando o param nao existe ou e invalido,
 * empresa = null e loading = false (no-op). Quando existe mas a busca de
 * contexto ainda nao voltou, loading = true.
 *
 * **Aplicacao**: o componente deve fazer `setEmpresa(empresa)` UMA VEZ
 * quando empresa muda de null pra um valor — depois disso o usuario pode
 * trocar manualmente via select sem ser sobrescrito. Padrao com flag:
 *
 *   const { empresa: empresaInicial } = useEmpresaFromSearchParam();
 *   const aplicado = useRef(false);
 *   useEffect(() => {
 *     if (!aplicado.current && empresaInicial) {
 *       setEmpresa(empresaInicial);
 *       aplicado.current = true;
 *     }
 *   }, [empresaInicial]);
 */
export function useEmpresaFromSearchParam(): {
  empresa: IEmpresaOption | null;
  loading: boolean;
} {
  const [params] = useSearchParams();
  const raw = params.get('empresa');
  const id = raw ? Number(raw) : NaN;
  const empresaId = Number.isFinite(id) && id > 0 ? id : undefined;

  const { data: ctx, isLoading } = useContaCilEmpresaContexto(empresaId ?? null);

  if (!empresaId) {
    return { empresa: null, loading: false };
  }
  if (!ctx) {
    return { empresa: null, loading: isLoading };
  }
  return {
    empresa: {
      value: ctx.empresa_id,
      label: ctx.razao_social || ctx.nome || `Empresa ${ctx.empresa_id}`,
      external_id: ctx.external_id || '',
    },
    loading: false,
  };
}

/**
 * Acucar sintatico: aplica o `?empresa=` UMA VEZ no estado local da tela.
 * Evita o boilerplate de useRef + useEffect em cada uma das 4 telas alvo.
 */
export function useAplicarEmpresaParam(setEmpresa: (e: IEmpresaOption | null) => void): void {
  const { empresa } = useEmpresaFromSearchParam();
  const aplicado = useRef(false);
  useEffect(() => {
    if (!aplicado.current && empresa) {
      setEmpresa(empresa);
      aplicado.current = true;
    }
  }, [empresa, setEmpresa]);
}
